import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  Autocomplete,
} from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { baseurl } from "../../Basurl/Baseurl";
import Loader from "../../components/Loader";
import { getUserId } from "../../utils/authStorage";
import { GetAllStaffUser } from "../../reducer/StaffSlice";
import { GetAllPatients } from "../../reducer/PatientsSlice";
import {
  buildActivityLogsExcelHtml,
  downloadActivityLogsFile,
  extractActivityLogPagination,
  extractActivityLogs,
  formatActivityLogDate,
  getActivityLogAction,
  getActivityLogDate,
  getActivityLogDescription,
  getActivityLogId,
  getActivityLogIp,
  getActivityLogLocation,
  getActivityLogMapUrl,
  getActivityLogModule,
  getActivityLogUser,
} from "./activityLogHelpers";

const truncateText = (text, maxLength = 40) => {
  if (!text) return "-";
  const value = String(text);
  return value.length > maxLength
    ? `${value.substring(0, maxLength)}...`
    : value;
};

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "CREATE", label: "CREATE" },
  { value: "UPDATE", label: "UPDATE" },
  { value: "DELETE", label: "DELETE" },
  { value: "STATUS_CHANGE", label: "STATUS_CHANGE" },
  { value: "UPLOAD", label: "UPLOAD" },
  { value: "APPROVE", label: "APPROVE" },
  { value: "REJECT", label: "REJECT" },
  { value: "ASSIGN", label: "ASSIGN" },
  { value: "LOGIN", label: "LOGIN" },
  { value: "LOGOUT", label: "LOGOUT" },
  { value: "EXPORT", label: "EXPORT" },
];

const autocompleteSx = {
  "& .MuiInputBase-root": { height: 40 },
  "& .MuiInputBase-input": { padding: "6px 12px" },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const PAGE_SIZE = 10;
const EXPORT_LIMIT = 1000;

const getStaffId = (staff) => staff?._id ?? staff?.id ?? staff?.userId ?? "";
const getStaffName = (staff) =>
  staff?.name ?? staff?.userName ?? staff?.fullName ?? "";
const getPatientOptionLabel = (p) => {
  if (!p) return "";
  const name = p.patient_name || p.name || "";
  const id = p.patientId || p.patientNumber || "";
  if (name && id) return `${name} (${id})`;
  return name || id || "";
};

export default function ActivityLog() {
  const dispatch = useDispatch();
  const { staff } = useSelector((state) => state.staff);
  const { patient } = useSelector((state) => state.patient);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [action, setAction] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const staffOptions = useMemo(
    () => (Array.isArray(staff) ? staff : []),
    [staff],
  );
  const patientOptions = useMemo(
    () => (Array.isArray(patient) ? patient : []),
    [patient],
  );

  const filterUserId = getStaffId(selectedStaff);
  const patientId =
    selectedPatient?.patientId || selectedPatient?.patientNumber || "";

  useEffect(() => {
    dispatch(GetAllStaffUser());
    dispatch(GetAllPatients({ page: 1, limit: 500, search: "" }));
  }, [dispatch]);

  /**
   * Staff filter API:
   * GET /get_activity_logs?filterByUser=true&filterUserId=<staffMongoId>&page=1&limit=10
   */
  const buildParams = useCallback(
    ({ page: pageNum, limit }) => {
      const params = {
        userId: getUserId(),
        page: pageNum,
        limit,
      };

      if (action) {
        params.action = action;
      }

      if (patientId) {
        params.patientId = patientId;
      }

      if (filterUserId) {
        params.filterByUser = true;
        params.filterUserId = filterUserId;
      }

      return params;
    },
    [action, patientId, filterUserId],
  );

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseurl}get_activity_logs`, {
          headers: authHeaders(),
          signal: controller.signal,
          params: buildParams({ page, limit: PAGE_SIZE }),
        });

        const logs = extractActivityLogs(response.data);
        const pagination = extractActivityLogPagination(
          response.data,
          PAGE_SIZE,
        );
        const pages = Math.max(1, pagination.totalPages || 1);

        setRows(logs);
        setTotalPages(pages);
        setTotalRecords(pagination.totalRecords || logs.length);

        if (page > pages) {
          setPage(pages);
        }
      } catch (error) {
        if (
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }
        setRows([]);
        setTotalPages(1);
        setTotalRecords(0);
        toast.error(
          error.response?.data?.message || "Failed to fetch activity logs",
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => controller.abort();
  }, [page, action, patientId, filterUserId, buildParams]);

  const handleActionChange = (event) => {
    setAction(event.target.value);
    setPage(1);
  };

  const handleStaffChange = (_, value) => {
    setSelectedStaff(value);
    setPage(1);
  };

  const handlePatientChange = (_, value) => {
    setSelectedPatient(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setAction("");
    setSelectedStaff(null);
    setSelectedPatient(null);
    setPage(1);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${baseurl}get_activity_logs`, {
        headers: authHeaders(),
        params: buildParams({ page: 1, limit: EXPORT_LIMIT }),
      });
      const logs = extractActivityLogs(response.data);

      if (!logs.length) {
        toast.info("No activity logs available to export");
        return;
      }

      const excelHtml = `\uFEFF${buildActivityLogsExcelHtml(logs)}`;
      const stamp = new Date().toISOString().slice(0, 10);
      downloadActivityLogsFile(
        excelHtml,
        `activity-logs-${stamp}.xls`,
        "application/vnd.ms-excel;charset=utf-8;",
      );
      toast.success("Activity logs exported to Excel");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to export activity logs",
      );
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = Boolean(action || selectedStaff || selectedPatient);
  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalRecords);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row gx-3">
          <div className="col-md-12">
            <div className="country-top">
              <div className="topmainhd mb-0">
                <h6>Activity Log</h6>
              </div>
              <div className="search-btn-main">
                <button
                  type="button"
                  className="add-button"
                  disabled={exporting || loading}
                  onClick={handleExport}
                >
                  <i className="fa fa-file-excel-o me-2"></i>
                  {exporting ? "Exporting..." : "Export Excel"}
                </button>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="main_content mb-3">
              <div className="row gx-3 align-items-end gy-3">
                <div className="col-md-3">
                  <div className="field-set">
                    <label>Staff</label>
                    <Autocomplete
                      options={staffOptions}
                      value={selectedStaff}
                      getOptionLabel={(option) => getStaffName(option) || ""}
                      isOptionEqualToValue={(option, value) =>
                        String(getStaffId(option)) === String(getStaffId(value))
                      }
                      onChange={handleStaffChange}
                      ListboxProps={{ style: { maxHeight: 250 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select staff"
                          sx={autocompleteSx}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="field-set">
                    <label>Patient</label>
                    <Autocomplete
                      options={patientOptions}
                      value={selectedPatient}
                      getOptionLabel={getPatientOptionLabel}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.patientId || "") ===
                        String(value?.patientId || "")
                      }
                      filterOptions={(options, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase();
                        if (!q) return options;
                        return options.filter((p) => {
                          const name = String(
                            p.patient_name || p.name || "",
                          ).toLowerCase();
                          const id = String(
                            p.patientId || p.patientNumber || "",
                          ).toLowerCase();
                          return name.includes(q) || id.includes(q);
                        });
                      }}
                      onChange={handlePatientChange}
                      ListboxProps={{ style: { maxHeight: 250 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select patient"
                          sx={autocompleteSx}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="field-set">
                    <label>Action</label>
                    <FormControl fullWidth size="small">
                      <Select
                        value={action}
                        onChange={handleActionChange}
                        displayEmpty
                        input={<OutlinedInput placeholder="All Actions" />}
                        sx={{ height: 40 }}
                        className="form-control"
                      >
                        {ACTION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || "all"} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                <div className="col-md-3 mb-4 d-flex gap-2">
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="add-button"
                      style={{ height: 40 }}
                      onClick={handleClearFilters}
                    >
                      <i className="fa fa-eraser me-2"></i>Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="main_content">
              <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                <small className="text-muted">
                  {loading
                    ? "Loading..."
                    : totalRecords > 0
                      ? `Showing ${rangeStart}-${rangeEnd} of ${totalRecords}`
                      : "No records"}
                </small>
                <small className="text-muted">{PAGE_SIZE} per page</small>
              </div>
              <div className="table-responsive">
                <TableContainer component={Paper}>
                  <Table stickyHeader className="table-no-card">
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr.No.</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Module</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>IP Address</TableCell>
                        <TableCell>Location (Lat/Long)</TableCell>
                      </TableRow>
                    </TableHead>
                    {loading ? (
                      <Loader />
                    ) : (
                      <TableBody>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No activity logs found
                            </TableCell>
                          </TableRow>
                        ) : (
                          rows.map((log, index) => (
                            <TableRow key={getActivityLogId(log) || index}>
                              <TableCell>
                                {(page - 1) * PAGE_SIZE + index + 1}
                              </TableCell>
                              <TableCell title={getActivityLogUser(log)}>
                                {truncateText(getActivityLogUser(log))}
                              </TableCell>
                              <TableCell title={getActivityLogAction(log)}>
                                {truncateText(getActivityLogAction(log), 18)}
                              </TableCell>
                              <TableCell title={getActivityLogModule(log)}>
                                {truncateText(getActivityLogModule(log), 25)}
                              </TableCell>
                              <TableCell title={getActivityLogDescription(log)}>
                                {truncateText(getActivityLogDescription(log))}
                              </TableCell>
                              <TableCell style={{ whiteSpace: "nowrap" }}>
                                {formatActivityLogDate(getActivityLogDate(log))}
                              </TableCell>
                              <TableCell title={getActivityLogIp(log)}>
                                {truncateText(getActivityLogIp(log), 20)}
                              </TableCell>
                              <TableCell title={getActivityLogLocation(log)}>
                                {(() => {
                                  const locText = getActivityLogLocation(log);
                                  const mapUrl = getActivityLogMapUrl(log);

                                  if (!locText || locText === "-" || locText === "—") {
                                    return "-";
                                  }

                                  if (mapUrl) {
                                    return (
                                      <a
                                        href={mapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "5px",
                                          color: "#1e40af",
                                          fontWeight: 600,
                                          textDecoration: "none",
                                          padding: "3px 8px",
                                          borderRadius: "4px",
                                          backgroundColor: "rgba(30, 64, 175, 0.08)",
                                          transition: "all 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor =
                                            "rgba(30, 64, 175, 0.15)";
                                          e.currentTarget.style.textDecoration =
                                            "underline";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor =
                                            "rgba(30, 64, 175, 0.08)";
                                          e.currentTarget.style.textDecoration =
                                            "none";
                                        }}
                                        title={`Click to open ${locText} in Google Maps`}
                                      >
                                        <i
                                          className="fa fa-map-marker"
                                          style={{
                                            color: "#ef4444",
                                            fontSize: "14px",
                                          }}
                                        />
                                        <span>{truncateText(locText, 25)}</span>
                                        <i
                                          className="fa fa-external-link"
                                          style={{
                                            fontSize: "11px",
                                            opacity: 0.6,
                                          }}
                                        />
                                      </a>
                                    );
                                  }

                                  return truncateText(locText, 28);
                                })()}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    )}
                  </Table>
                  {!loading && totalPages > 1 ? (
                    <Stack spacing={2}>
                      <Pagination
                        className="page-nation"
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Stack>
                  ) : null}
                </TableContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
